import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

interface MobileDrawerProps {
  open: boolean;
  /** Fired on backdrop click and Escape. */
  onClose: () => void;
  /** Accessible name for the navigation panel. */
  label: string;
  children: ReactNode;
}

/**
 * Slide-in navigation drawer for small screens (`md:hidden`). The shells own
 * the open state (and close it on route change); this component handles the
 * backdrop, Escape, focus hand-off and body scroll-lock.
 */
export function MobileDrawer({ open, onClose, label, children }: MobileDrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    // Scroll-lock while the drawer is open; restore the previous value on close.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <button
        type="button"
        aria-label="Cerrar menú"
        className="absolute inset-0 h-full w-full cursor-default bg-slate-900/40"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] animate-slide-up-fade-in flex-col overflow-y-auto bg-white shadow-xl outline-none"
      >
        {children}
      </div>
    </div>
  );
}
